import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getDataProvider, subscribeToTable, isStandaloneMode, type Subscription, type ChangePayload } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { useImageLibraryStore } from './imageLibrary'
import { useScenesStore } from './scenes'
import { useSceneDisplayConfigsStore } from './sceneDisplayConfigs'
import {
  cloneDisplayLayer,
  shallowCloneDisplayLayer,
  normalizeDisplayLayer
} from './sceneDisplayConfigs'
import type {
  DisplayLayer,
  CreateDisplayLayerData,
  UpdateDisplayLayerData
} from './sceneDisplayConfigs'

export const useDisplayLayersStore = defineStore('displayLayers', () => {
  const authStore = useAuthStore()
  const imageLibraryStore = useImageLibraryStore()
  // Keep as regular ref since we use Map.set() for incremental updates
  // The layer arrays inside are immutable (replaced, not mutated)
  const layers = ref<Map<string, DisplayLayer[]>>(new Map()) // Key: scene_display_config_id
  const overrides = ref<Map<string, any[]>>(new Map()) // Key: scene_display_config_id
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const initializationPromise = ref<Promise<void> | null>(null)
  
  // Deduplication map for inheritance fetches
  const inFlightInheritanceFetches = new Map<string, Promise<DisplayLayer[]>>()

  // Transform database layer to display layer with computed properties
  const transformLayer = (dbLayer: Record<string, unknown>): DisplayLayer => {
    const { content: resolvedContent, storagePath } = resolveLayerContent(dbLayer)

    const normalized = normalizeDisplayLayer({
      ...dbLayer,
      type: dbLayer.layer_type,
      content: resolvedContent,
      storage_path: storagePath,
      x: dbLayer.position_x,
      y: dbLayer.position_y,
      zIndex: dbLayer.z_index,
      crop: dbLayer.crop_settings,
      objectFit: dbLayer.object_fit
    } as DisplayLayer)

    return {
      ...normalized,
      content: resolvedContent,
      storage_path: storagePath
    }
  }

  // Resolve content URL and storage path based on layer type
  const resolveLayerContent = (layer: Record<string, unknown>): { content: string; storagePath?: string } => {
    if ((layer.layer_type === 'image' || layer.layer_type === 'video') && layer.content_source) {
      // For image/video layers, content_source is an image_library.id
      const image = imageLibraryStore.images.find(img => img.id === layer.content_source)
      return { 
        content: image?.url || '', 
        storagePath: image?.storage_path 
      }
    } else if (layer.layer_type === 'youtube') {
      // For youtube layers, content_source is the URL
      return { content: String(layer.content_source || '') }
    } else if (layer.layer_type === 'text') {
      // For text layers, content_source is the text content
      return { content: String(layer.content_source || '') }
    }
    return { content: String(layer.content_source || '') }
  }

  // Fetch all layers and overrides for the user
  const fetchAllLayers = async () => {
    if (initialized.value) return
    if (initializationPromise.value) return initializationPromise.value
    if (!authStore.user?.id) return

    loading.value = true
    error.value = null

    initializationPromise.value = (async () => {
      try {
        const provider = getDataProvider()
        
        // Ensure image library is loaded
        if (imageLibraryStore.images.length === 0) {
          await imageLibraryStore.fetchImages()
        }

        // Fetch all layers for user's scenes
        const layersData = await provider.query<any>('display_layers', {
          orderBy: [{ column: 'z_index', ascending: true }]
        })

        // Group layers by config ID
        const layersMap = new Map<string, DisplayLayer[]>()
        layersData?.forEach(layer => {
          const configId = layer.scene_display_config_id
          if (!layersMap.has(configId)) {
            layersMap.set(configId, [])
          }
          // transformLayer already creates a new normalized object, no need to clone
          layersMap.get(configId)?.push(transformLayer(layer))
        })
        
        // Update layers state
        layers.value = layersMap

        // Fetch all overrides for user's scenes
        const overridesData = await provider.query<any>('display_layer_overrides')

        // Group overrides by config ID
        const overridesMap = new Map<string, any[]>()
        overridesData?.forEach(override => {
          const configId = override.scene_display_config_id
          if (!overridesMap.has(configId)) {
            overridesMap.set(configId, [])
          }
          overridesMap.get(configId)?.push(override)
        })

        overrides.value = overridesMap
        initialized.value = true
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to fetch all layers'
        console.error('❌ [displayLayers] Failed to fetch all layers:', err)
        throw err
      } finally {
        loading.value = false
        initializationPromise.value = null
      }
    })()

    return initializationPromise.value
  }

  // Fetch layers for a specific scene display config
  const fetchLayersForDisplay = async (sceneDisplayConfigId: string): Promise<DisplayLayer[]> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      // Ensure image library is loaded before fetching layers
      if (imageLibraryStore.images.length === 0 && authStore.user?.id) {
        await imageLibraryStore.fetchImages()
      }

      const data = await provider.query<any>('display_layers', {
        filters: [{ column: 'scene_display_config_id', operator: 'eq', value: sceneDisplayConfigId }],
        orderBy: [{ column: 'z_index', ascending: true }]
      })

      // transformLayer already creates a new normalized object, no need to clone
      const transformedLayers = (data || []).map(layer => transformLayer(layer))
      
      // Cache in local state
      layers.value.set(sceneDisplayConfigId, transformedLayers)
      
      return transformedLayers
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch layers'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Fetch layers WITH inheritance from parent scenes
  const fetchLayersWithInheritance = async (
    sceneDisplayConfigId: string,
    sceneId: string,
    displayId: string
  ): Promise<DisplayLayer[]> => {
    const fetchKey = `${sceneDisplayConfigId}-${sceneId}-${displayId}`
    
    if (inFlightInheritanceFetches.has(fetchKey)) {
      return await inFlightInheritanceFetches.get(fetchKey)!
    }

    // If initialization is in progress, wait for it
    if (initializationPromise.value) {
      await initializationPromise.value
    }

    // If initialized, we can do this synchronously (mostly)
    if (initialized.value) {
      const scenesStore = useScenesStore()
      const configsStore = useSceneDisplayConfigsStore()
      
      // 1. Build path from Root to Leaf
      const scenePath: string[] = []
      let currentId: string | null = sceneId
      while (currentId) {
        scenePath.unshift(currentId)
        const scene = scenesStore.scenes.get(currentId)
        if (!scene) break
        currentId = scene.parent_id
      }

      // 2. Accumulate layers and apply overrides down the tree
      const accumulatedLayers = new Map<string, DisplayLayer>()

      for (const currSceneId of scenePath) {
        const config = configsStore.getConfigForSceneAndDisplay(currSceneId, displayId)
        if (!config) {
          continue
        }

        // a. Add/Update base layers defined in this scene
        const sceneLayers = layers.value.get(config.id) || []
        
        for (const layer of sceneLayers) {
          // Check if this layer replaces an existing inherited layer
          if ((layer as any).overrides_layer_id) {
            accumulatedLayers.delete((layer as any).overrides_layer_id)
          }
          // Use shallow clone since we're computing read-only display data
          accumulatedLayers.set(layer.id, shallowCloneDisplayLayer(layer))
        }

        // b. Apply overrides defined in this scene
        const sceneOverrides = overrides.value.get(config.id) || []
        for (const override of sceneOverrides) {
          const targetLayer = accumulatedLayers.get(override.parent_layer_id)
          if (targetLayer) {
            // Apply override properties
            if (override.visible !== undefined && override.visible !== null) targetLayer.visible = override.visible
            if (override.position_x !== undefined && override.position_x !== null) {
              targetLayer.position_x = override.position_x
              targetLayer.x = override.position_x
            }
            if (override.position_y !== undefined && override.position_y !== null) {
              targetLayer.position_y = override.position_y
              targetLayer.y = override.position_y
            }
            if (override.width !== undefined && override.width !== null) targetLayer.width = override.width
            if (override.height !== undefined && override.height !== null) targetLayer.height = override.height
            if (override.z_index !== undefined && override.z_index !== null) {
              targetLayer.z_index = override.z_index
              targetLayer.zIndex = override.z_index
            }
            if (override.crop_settings) {
              targetLayer.crop_settings = override.crop_settings
              targetLayer.crop = override.crop_settings
            }
            if (override.effects) {
              targetLayer.effects = { ...targetLayer.effects, ...override.effects }
            }
          }
        }
      }

      // 3. Finalize layers
      const finalLayers = Array.from(accumulatedLayers.values()).map(layer => {
        const isInherited = layer.scene_display_config_id !== sceneDisplayConfigId
        
        let sourceSceneName: string | undefined
        let sourceSceneId: string | null = null

        if (isInherited) {
           // Find the scene that owns this layer
           for (const sid of scenePath) {
               const cfg = configsStore.getConfigForSceneAndDisplay(sid, displayId)
               if (cfg && cfg.id === layer.scene_display_config_id) {
                   const s = scenesStore.scenes.get(sid)
                   if (s) {
                       sourceSceneName = s.name
                       sourceSceneId = s.id
                   }
                   break
               }
           }
        }

        // Check if the CURRENT scene has an override for this layer
        const leafOverrides = overrides.value.get(sceneDisplayConfigId) || []
        const hasLeafOverride = leafOverrides.some(o => o.parent_layer_id === layer.id)

        return {
          ...layer,
          is_inherited: isInherited,
          inherited_from_scene_id: sourceSceneId,
          parent_scene_name: sourceSceneName,
          can_modify: !isInherited,
          has_overrides: hasLeafOverride
        }
      })
      
      const sortedLayers = finalLayers.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
      
      // DON'T update the cache here - it causes infinite loops in watchers
      // The sync path just computes and returns, the raw data is already in the cache
      
      return sortedLayers
    }
    
    loading.value = true
    error.value = null

    const fetchPromise = (async () => {
      try {
      const provider = getDataProvider()
      
      // Ensure image library is loaded
      if (imageLibraryStore.images.length === 0 && authStore.user?.id) {
        await imageLibraryStore.fetchImages()
      }

      const { useScenesStore } = await import('./scenes')
      const scenesStore = useScenesStore()
      const { useSceneDisplayConfigsStore } = await import('./sceneDisplayConfigs')
      const configsStore = useSceneDisplayConfigsStore()
      
      // Ensure configs are loaded for this scene (will also fetch parent configs if needed)
      await configsStore.fetchConfigsForScene(sceneId)
      
      const allLayers: DisplayLayer[] = []
      const processedScenes = new Set<string>()
      
      // Traverse up the scene hierarchy
      let currentSceneId: string | null = sceneId
      
      while (currentSceneId) {
        // Prevent infinite loops
        if (processedScenes.has(currentSceneId)) break
        processedScenes.add(currentSceneId)
        
        const currentScene = scenesStore.scenes.get(currentSceneId)
        if (!currentScene) break
        
        // Ensure configs are loaded for this scene too (for parent scenes)
        if (currentSceneId !== sceneId) {
          await configsStore.fetchConfigsForScene(currentSceneId)
        }
        
        // Get the actual config for this scene + display
        const sceneConfig = configsStore.getConfigForSceneAndDisplay(currentSceneId, displayId)
        if (!sceneConfig) {
          // No config exists for this scene/display combo, move to parent
          currentSceneId = currentScene.parent_id
          continue
        }
        
        // Fetch layers for this scene's display config
        const sceneLayers = await provider.query<any>('display_layers', {
          filters: [{ column: 'scene_display_config_id', operator: 'eq', value: sceneConfig.id }],
          orderBy: [{ column: 'z_index', ascending: true }]
        })
        
        if (sceneLayers && sceneLayers.length > 0) {
          // Transform and mark layers as inherited if from parent
          const isInherited = currentSceneId !== sceneId
          
          sceneLayers.forEach(dbLayer => {
            // transformLayer already creates a new object, use shallow clone for metadata
            const layer = transformLayer(dbLayer)
            const layerWithMeta: DisplayLayer = {
              ...layer,
              inherited_from_scene_id: isInherited ? currentSceneId : null,
              is_inherited: isInherited,
              can_modify: !isInherited,
              parent_scene_name: isInherited ? currentScene.name : undefined
            }
            allLayers.push(layerWithMeta)
          })
        }
        
        // Move to parent scene
        currentSceneId = currentScene.parent_id
      }
      
      // Reverse so parent layers come first (lower z-index baseline)
      const inheritedLayers = allLayers.reverse()
      
      // Fetch overrides for the current scene's display config
      const fetchedOverrides = await provider.query<any>('display_layer_overrides', {
        filters: [{ column: 'scene_display_config_id', operator: 'eq', value: sceneDisplayConfigId }]
      })

      const overrideMap = new Map<string, any>()
      fetchedOverrides.forEach(override => {
        overrideMap.set(override.parent_layer_id, override)
      })

      // Apply overrides to inherited layers and filter out old-style overridden layers
      const currentSceneLayers = inheritedLayers.filter(layer => !layer.is_inherited)
      const overriddenLayerIds = new Set(
        currentSceneLayers
          .map(layer => layer.overrides_layer_id)
          .filter((id): id is string => id != null)
      )
      
      // Process layers: apply overrides and filter out old-style overrides
      const finalLayers = inheritedLayers
        .filter(layer => !layer.is_inherited || !overriddenLayerIds.has(layer.id))
        .map(layer => {
          // Only apply overrides to inherited layers
          if (!layer.is_inherited) return layer

          const override = overrideMap.get(layer.id)
          if (!override) return layer

          // Merge override fields with inherited layer (only non-null overrides)
          const overriddenLayer: DisplayLayer = {
            ...layer,
            has_overrides: true
          }

          // Apply each override field if it's not null/undefined
          if (override.visible !== undefined && override.visible !== null) {
            overriddenLayer.visible = override.visible
          }
          if (override.position_x !== undefined && override.position_x !== null) {
            overriddenLayer.position_x = override.position_x
            overriddenLayer.x = override.position_x
          }
          if (override.position_y !== undefined && override.position_y !== null) {
            overriddenLayer.position_y = override.position_y
            overriddenLayer.y = override.position_y
          }
          if (override.width !== undefined && override.width !== null) {
            overriddenLayer.width = override.width
          }
          if (override.height !== undefined && override.height !== null) {
            overriddenLayer.height = override.height
          }
          if (override.z_index !== undefined && override.z_index !== null) {
            overriddenLayer.z_index = override.z_index
            overriddenLayer.zIndex = override.z_index
          }
          if (override.crop_settings) {
            overriddenLayer.crop_settings = override.crop_settings
            overriddenLayer.crop = override.crop_settings
          }
          if (override.effects) {
            overriddenLayer.effects = { ...layer.effects, ...override.effects }
          }

          return overriddenLayer
        })
      
      // Sort by z_index (ascending) after applying all overrides
      const sortedLayers = [...finalLayers].sort((a, b) => {
        const aZ = a.z_index ?? a.zIndex ?? 0
        const bZ = b.z_index ?? b.zIndex ?? 0
        return aZ - bZ
      })
      
      // Cache in local state
      layers.value.set(sceneDisplayConfigId, sortedLayers)
      
      return sortedLayers
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to fetch layers'
        throw err
      } finally {
        loading.value = false
        inFlightInheritanceFetches.delete(fetchKey)
      }
    })()

    inFlightInheritanceFetches.set(fetchKey, fetchPromise)
    return await fetchPromise
  }

  // Create a new layer
  const createLayer = async (layerData: CreateDisplayLayerData): Promise<DisplayLayer> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      const result = await provider.insert<any>('display_layers', layerData)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to create layer')
      }

  const newLayer = cloneDisplayLayer(transformLayer(result.data))

      // Add to local cache
      const existingLayers = layers.value.get(layerData.scene_display_config_id) || []
      layers.value.set(layerData.scene_display_config_id, [...existingLayers, newLayer])

      return newLayer
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create layer'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Update an existing layer (with selective overrides for inherited layers)
  const updateLayer = async (
    layerId: string, 
    updates: UpdateDisplayLayerData,
    currentSceneDisplayConfigId?: string,
    sceneId?: string,
    displayId?: string,
    layerHint?: DisplayLayer  // Optional: provide layer with inheritance metadata to avoid cache lookup
  ): Promise<DisplayLayer> => {
    loading.value = true
    error.value = null

    try {
      // Use provided layer or find in cache
      let targetLayer: DisplayLayer | undefined = layerHint
      
      console.log('[displayLayers] updateLayer - layerHint received:', {
        layerHint_exists: !!layerHint,
        layerHint_is_inherited: layerHint?.is_inherited,
        layerHint_keys: layerHint ? Object.keys(layerHint) : []
      })
      
      if (!targetLayer) {
        for (const configLayers of layers.value.values()) {
          targetLayer = configLayers.find(layer => layer.id === layerId)
          if (targetLayer) break
        }
      }

      console.log('[displayLayers] updateLayer called:', {
        layerId,
        updates,
        targetLayer: targetLayer ? {
          id: targetLayer.id,
          name: targetLayer.name,
          is_inherited: targetLayer.is_inherited,
          inherited_from_scene_id: targetLayer.inherited_from_scene_id,
          has_overrides: targetLayer.has_overrides
        } : null,
        currentSceneDisplayConfigId,
        sceneId,
        displayId,
        willCreateOverride: targetLayer?.is_inherited && currentSceneDisplayConfigId && sceneId && displayId,
        usedLayerHint: !!layerHint
      })

      // If layer is inherited, create/update an override record instead of copying the layer
      if (targetLayer?.is_inherited && currentSceneDisplayConfigId && sceneId && displayId) {
        console.log('[displayLayers] Layer is inherited, creating/updating override')
        
        const provider = getDataProvider()
        
        // Check if override already exists
        const existingOverrides = await provider.query<any>('display_layer_overrides', {
          filters: [
            { column: 'scene_display_config_id', operator: 'eq', value: currentSceneDisplayConfigId },
            { column: 'parent_layer_id', operator: 'eq', value: layerId }
          ]
        })
        const existingOverride = existingOverrides.length > 0 ? existingOverrides[0] : null

        console.log('[displayLayers] Existing override:', existingOverride)

        // Build override data - only include fields that are being updated
        const overrideData: Record<string, any> = {
          scene_display_config_id: currentSceneDisplayConfigId,
          parent_layer_id: layerId
        }

        // Only include fields that are explicitly being updated
        if (updates.visible !== undefined) overrideData.visible = updates.visible
        if (updates.position_x !== undefined) overrideData.position_x = updates.position_x
        if (updates.position_y !== undefined) overrideData.position_y = updates.position_y
        if (updates.width !== undefined) overrideData.width = updates.width
        if (updates.height !== undefined) overrideData.height = updates.height
        if (updates.z_index !== undefined) overrideData.z_index = updates.z_index
        if (updates.crop_settings !== undefined) overrideData.crop_settings = updates.crop_settings
        if (updates.effects !== undefined) overrideData.effects = updates.effects

        console.log('[displayLayers] Override data to insert/update:', overrideData)

        let savedOverride: any

        if (existingOverride) {
          // Update existing override (merge with existing overrides)
          const result = await provider.update<any>('display_layer_overrides', existingOverride.id, overrideData)
          
          if (!result.success) {
            console.error('[displayLayers] Failed to update override:', result.error)
            throw new Error(result.error)
          }
          savedOverride = result.data
        } else {
          // Create new override
          const result = await provider.insert<any>('display_layer_overrides', overrideData)
          
          if (!result.success) {
            console.error('[displayLayers] Failed to insert override:', result.error)
            throw new Error(result.error)
          }
          savedOverride = result.data
        }

        // Update local overrides cache immediately
        const currentOverrides = overrides.value.get(currentSceneDisplayConfigId) || []
        const overrideIndex = currentOverrides.findIndex(o => o.id === savedOverride.id)
        
        if (overrideIndex !== -1) {
          currentOverrides[overrideIndex] = savedOverride
        } else {
          currentOverrides.push(savedOverride)
        }
        overrides.value.set(currentSceneDisplayConfigId, [...currentOverrides])

        // Get merged and sorted layers with inheritance applied (not from store)
        const refreshedLayers = await fetchLayersWithInheritance(currentSceneDisplayConfigId, sceneId, displayId)
        
        // Return the updated inherited layer (now with overrides applied)
        const updatedLayer = refreshedLayers.find(l => l.id === layerId)
        if (updatedLayer) {
          return updatedLayer
        }
        
        throw new Error('Failed to find updated inherited layer')
      }

      // Normal update for non-inherited layers
      const provider = getDataProvider()
      const result = await provider.update<any>('display_layers', layerId, updates)

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to update layer')
      }

      const updatedLayer = cloneDisplayLayer(transformLayer(result.data))

      // Update local cache
      for (const [configId, configLayers] of layers.value.entries()) {
        const layerIndex = configLayers.findIndex(layer => layer.id === layerId)
        if (layerIndex !== -1) {
          const newLayers = [...configLayers]
          newLayers[layerIndex] = updatedLayer
          layers.value.set(configId, newLayers)
          break
        }
      }

      return updatedLayer
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update layer'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Delete a layer (or delete override to reset inherited layer)
  const deleteLayer = async (
    layerId: string, 
    currentSceneDisplayConfigId?: string,
    sceneId?: string,
    displayId?: string
  ): Promise<void> => {
    console.log('[displayLayers] 🗑️ deleteLayer START', {
      layerId,
      currentSceneDisplayConfigId,
      sceneId,
      displayId
    })
    
    loading.value = true
    error.value = null

    try {
      // Find the layer to check if it's inherited
      let targetLayer: DisplayLayer | undefined
      let foundInConfigId: string | undefined
      for (const [configId, configLayers] of layers.value.entries()) {
        targetLayer = configLayers.find(layer => layer.id === layerId)
        if (targetLayer) {
          foundInConfigId = configId
          break
        }
      }

      console.log('[displayLayers] 🔍 Found target layer:', {
        found: !!targetLayer,
        foundInConfigId,
        layerName: targetLayer?.name,
        is_inherited: targetLayer?.is_inherited,
        has_overrides: targetLayer?.has_overrides,
        scene_display_config_id: targetLayer?.scene_display_config_id
      })

      // If layer is inherited and has overrides, delete the override to reset to parent
      if (targetLayer?.is_inherited && targetLayer?.has_overrides && currentSceneDisplayConfigId) {
        console.log('[displayLayers] 🔄 Deleting override for inherited layer with overrides')
        
        const provider = getDataProvider()
        
        // Find the override to delete
        const existingOverrides = await provider.query<any>('display_layer_overrides', {
          filters: [
            { column: 'scene_display_config_id', operator: 'eq', value: currentSceneDisplayConfigId },
            { column: 'parent_layer_id', operator: 'eq', value: layerId }
          ]
        })
        
        if (existingOverrides.length > 0) {
          const result = await provider.delete('display_layer_overrides', existingOverrides[0].id)
          if (!result.success) throw new Error(result.error)
        }

        // Refresh to show reset layer
        if (sceneId && displayId) {
          console.log('[displayLayers] 🔄 Refreshing layers after override delete')
          await fetchLayersWithInheritance(currentSceneDisplayConfigId, sceneId, displayId)
        }
        
        return
      }

      // If layer is inherited but has no overrides, hide it by creating a visibility override
      if (targetLayer?.is_inherited && currentSceneDisplayConfigId) {
        console.log('[displayLayers] 👁️ Creating visibility override to hide inherited layer')
        
        const provider = getDataProvider()
        
        // Create override with just visible: false
        const result = await provider.insert<any>('display_layer_overrides', {
          scene_display_config_id: currentSceneDisplayConfigId,
          parent_layer_id: layerId,
          visible: false
        })

        if (!result.success) {
          console.error('[displayLayers] ❌ Failed to create visibility override:', result.error)
          throw new Error(result.error)
        }

        // Update local overrides cache
        const currentOverrides = overrides.value.get(currentSceneDisplayConfigId) || []
        overrides.value.set(currentSceneDisplayConfigId, [
          ...currentOverrides,
          { parent_layer_id: layerId, visible: false, scene_display_config_id: currentSceneDisplayConfigId }
        ])
        console.log('[displayLayers] ✅ Added visibility override to cache')

        // Refresh to hide the layer
        if (sceneId && displayId) {
          console.log('[displayLayers] 🔄 Refreshing layers after visibility override')
          await fetchLayersWithInheritance(currentSceneDisplayConfigId, sceneId, displayId)
        }
        
        return
      }

      // Normal delete for non-inherited layers
      console.log('[displayLayers] 🗑️ Deleting non-inherited layer from database')
      const provider = getDataProvider()
      const result = await provider.delete('display_layers', layerId)

      if (!result.success) throw new Error(result.error)

      // Remove from the RAW layers cache (the source of truth)
      // We need to find which config this layer actually belongs to (by scene_display_config_id)
      const targetConfigId = targetLayer?.scene_display_config_id
      
      console.log('[displayLayers] 📦 Updating local cache after delete')
      console.log('[displayLayers] 📦 Target config ID from layer:', targetConfigId)
      
      if (targetConfigId) {
        const configLayers = layers.value.get(targetConfigId) || []
        const filteredLayers = configLayers.filter(layer => layer.id !== layerId)
        console.log('[displayLayers] 🗑️ Removed layer from its own config:', {
          configId: targetConfigId,
          before: configLayers.length,
          after: filteredLayers.length,
          removedLayerId: layerId
        })
        layers.value.set(targetConfigId, filteredLayers)
      }
      
      // Note: The caller (handleDeleteLayer) will call fetchLayersWithInheritance 
      // to get the updated computed layers
      
      console.log('[displayLayers] 📦 Cache state AFTER delete:', {
        configIds: Array.from(layers.value.keys()),
        layerCounts: Array.from(layers.value.entries()).map(([id, l]) => ({ id, count: l.length }))
      })
    } catch (err) {
      console.error('[displayLayers] ❌ deleteLayer failed:', err)
      error.value = err instanceof Error ? err.message : 'Failed to delete layer'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Reorder layers by updating their z_index
  const reorderLayers = async (
    sceneDisplayConfigId: string, 
    layerIds: string[],
    sceneId?: string,
    displayId?: string
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      // Update z_index for each layer based on array position
      for (let index = 0; index < layerIds.length; index++) {
        await provider.update('display_layers', layerIds[index], { z_index: index })
      }

      // Refresh local cache - use inheritance-aware fetch if scene and display are provided
      if (sceneId && displayId) {
        await fetchLayersWithInheritance(sceneDisplayConfigId, sceneId, displayId)
      } else {
        await fetchLayersForDisplay(sceneDisplayConfigId)
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to reorder layers'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Duplicate a layer
  const duplicateLayer = async (layerId: string): Promise<DisplayLayer> => {
    const existingLayer = findLayerById(layerId)
    if (!existingLayer) {
      throw new Error('Layer not found')
    }

    // Create new layer data with slight offset
    const duplicateData: CreateDisplayLayerData = {
      scene_display_config_id: existingLayer.scene_display_config_id,
      layer_type: existingLayer.layer_type,
      name: `${existingLayer.name} Copy`,
      content_source: existingLayer.content_source,
      position_x: existingLayer.position_x + 10,
      position_y: existingLayer.position_y + 10,
      width: existingLayer.width,
      height: existingLayer.height,
      z_index: existingLayer.z_index + 1,
      visible: existingLayer.visible,
      locked: false,
      object_fit: existingLayer.object_fit,
      crop_settings: { ...existingLayer.crop_settings },
      effects: { ...existingLayer.effects }
    }

    return createLayer(duplicateData)
  }

  // Bulk delete layers
  const deleteLayers = async (layerIds: string[]): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const provider = getDataProvider()
      
      // Delete each layer individually (provider doesn't support 'in' filter directly)
      for (const layerId of layerIds) {
        const result = await provider.delete('display_layers', layerId)
        if (!result.success) {
          throw new Error(result.error || `Failed to delete layer ${layerId}`)
        }
      }

      // Remove from local cache
      for (const [configId, configLayers] of layers.value.entries()) {
        const filteredLayers = configLayers.filter(layer => !layerIds.includes(layer.id))
        layers.value.set(configId, filteredLayers)
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete layers'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Helper function to find a layer by ID across all configs
  const findLayerById = (layerId: string): DisplayLayer | null => {
    for (const configLayers of layers.value.values()) {
      const layer = configLayers.find(l => l.id === layerId)
      if (layer) return layer
    }
    return null
  }

  // Update layer z_index in the local cache (used after reordering)
  const updateLayerZIndexInCache = (layerId: string, newZIndex: number): void => {
    for (const [configId, configLayers] of layers.value.entries()) {
      const layerIndex = configLayers.findIndex(l => l.id === layerId)
      if (layerIndex !== -1) {
        const updatedLayers = [...configLayers]
        updatedLayers[layerIndex] = {
          ...updatedLayers[layerIndex],
          z_index: newZIndex,
          zIndex: newZIndex
        }
        layers.value.set(configId, updatedLayers)
        console.log('[displayLayers] 📝 Updated z_index in cache:', {
          layerId,
          configId,
          newZIndex
        })
        break
      }
    }
  }

  // Update override z_index in the local cache
  const updateOverrideZIndexInCache = (configId: string, parentLayerId: string, newZIndex: number): void => {
    const configOverrides = overrides.value.get(configId) || []
    const existingIndex = configOverrides.findIndex(o => o.parent_layer_id === parentLayerId)
    
    if (existingIndex !== -1) {
      const updatedOverrides = [...configOverrides]
      updatedOverrides[existingIndex] = {
        ...updatedOverrides[existingIndex],
        z_index: newZIndex
      }
      overrides.value.set(configId, updatedOverrides)
    } else {
      // Add new override
      overrides.value.set(configId, [
        ...configOverrides,
        { parent_layer_id: parentLayerId, z_index: newZIndex, scene_display_config_id: configId }
      ])
    }
    console.log('[displayLayers] 📝 Updated override z_index in cache:', {
      configId,
      parentLayerId,
      newZIndex
    })
  }

  // Get layers for a specific config from cache
  const getLayersForDisplay = (sceneDisplayConfigId: string): DisplayLayer[] => {
  return layers.value.get(sceneDisplayConfigId) || []
  }

  // Create layer from image library item
  const createImageLayer = async (
    sceneDisplayConfigId: string, 
    imageId: string, 
    position?: { x: number; y: number }
  ): Promise<DisplayLayer> => {
    console.log('🔥 CREATE IMAGE LAYER CALLED!', { sceneDisplayConfigId, imageId, position })
    
    const image = imageLibraryStore.images.find(img => img.id === imageId)
    if (!image) {
      console.error('❌ Image not found in library:', imageId)
      throw new Error('Image not found in library')
    }

    console.log('📸 [createImageLayer] Image data:', {
      id: image.id,
      name: image.original_name,
      width: image.width,
      height: image.height,
      hasWidth: image.width !== undefined,
      hasHeight: image.height !== undefined
    })

    // Use original image dimensions - no scaling
    const width = image.width || 400
    const height = image.height || 400

    console.log('📐 [createImageLayer] Using original dimensions:', {
      width,
      height
    })

    const layerData: CreateDisplayLayerData = {
      scene_display_config_id: sceneDisplayConfigId,
      layer_type: 'image',
      name: image.original_name,
      content_source: imageId,
      position_x: position?.x || 100,
      position_y: position?.y || 100,
      width: Math.round(width),
      height: Math.round(height),
      z_index: getNextZIndex(sceneDisplayConfigId),
      visible: true,
      locked: false,
      object_fit: 'fixed-aspect',
      crop_settings: { top: 0, left: 0, right: 0, bottom: 0 },
      effects: {}
    }

    console.log('💾 [createImageLayer] Layer data being sent to DB:', layerData)

    const result = await createLayer(layerData)
    console.log('✅ [createImageLayer] Layer created successfully:', result)
    return result
  }

  // Create YouTube layer
  const createYouTubeLayer = async (
    sceneDisplayConfigId: string,
    embedUrl: string,
    position?: { x: number; y: number }
  ): Promise<DisplayLayer> => {
    const layerData: CreateDisplayLayerData = {
      scene_display_config_id: sceneDisplayConfigId,
      layer_type: 'youtube',
      name: 'YouTube Video',
      content_source: embedUrl,
      position_x: position?.x || 100,
      position_y: position?.y || 100,
      width: 320,
      height: 180,
      z_index: getNextZIndex(sceneDisplayConfigId),
      visible: true,
      locked: false,
      object_fit: 'contain',
      crop_settings: { top: 0, left: 0, right: 0, bottom: 0 },
      effects: {}
    }

    return createLayer(layerData)
  }

  // Create video layer from uploaded video in image library
  const createVideoLayer = async (
    sceneDisplayConfigId: string,
    imageId: string,
    position?: { x: number; y: number }
  ): Promise<DisplayLayer> => {
    const layerData: CreateDisplayLayerData = {
      scene_display_config_id: sceneDisplayConfigId,
      layer_type: 'video',
      name: 'Video',
      content_source: imageId,
      position_x: position?.x || 100,
      position_y: position?.y || 100,
      width: 320,
      height: 180,
      z_index: getNextZIndex(sceneDisplayConfigId),
      visible: true,
      locked: false,
      object_fit: 'contain',
      crop_settings: { top: 0, left: 0, right: 0, bottom: 0 },
      effects: {}
    }

    return createLayer(layerData)
  }

  // Helper to get next z_index
  const getNextZIndex = (sceneDisplayConfigId: string): number => {
    const existingLayers = getLayersForDisplay(sceneDisplayConfigId)
    const maxZIndex = Math.max(...existingLayers.map(l => l.z_index || 0), 0)
    return maxZIndex + 1
  }

  // Realtime subscriptions
  let layersSubscription: Subscription | null = null
  let overridesSubscription: Subscription | null = null

  const subscribeToLayerUpdates = () => {
    if (!authStore.user) return

    // Subscribe to display_layers changes
    layersSubscription = subscribeToTable('display_layers', (payload: ChangePayload<any>) => {
      handleLayerChange(payload)
    })

    // Subscribe to display_layer_overrides changes
    overridesSubscription = subscribeToTable('display_layer_overrides', (payload: ChangePayload<any>) => {
      handleOverrideChange(payload)
    })

    // Return a cleanup function that unsubscribes from both
    return {
      unsubscribe: () => {
        layersSubscription?.unsubscribe()
        overridesSubscription?.unsubscribe()
      }
    }
  }

  /**
   * When a realtime INSERT arrives for an image layer and the image_library entry
   * isn't loaded (e.g. presenter view in a different tab), fetch the missing image
   * data, add it to the image library, and re-resolve the layer so it gets a
   * proper storage_path for cache-based loading.
   */
  const resolveAndUpdateMissingImage = async (configId: string, dbRecord: Record<string, unknown>) => {
    const provider = getDataProvider()
    const imageData = await provider.getById<any>('image_library', dbRecord.content_source as string)
    if (!imageData) {
      console.warn('⚠️ [displayLayers] Image not found in image_library:', dbRecord.content_source)
      return
    }

    // Add to imageLibraryStore so future resolves work
    if (!imageLibraryStore.images.find(img => img.id === imageData.id)) {
      imageLibraryStore.images.push({
        ...imageData,
        url: '',
        preview_url: undefined,
        storage_path: imageData.storage_path,
        preview_storage_path: imageData.preview_storage_path
      })
    }

    // Re-transform the layer now that the image is in the library
    const updatedLayer = cloneDisplayLayer(transformLayer(dbRecord))
    const currentLayers = layers.value.get(configId) || []
    const updatedLayers = currentLayers.map(l => l.id === updatedLayer.id ? updatedLayer : l)
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
    layers.value.set(configId, updatedLayers)

    console.log('✅ [displayLayers] Resolved missing image for layer:', updatedLayer.id, 'storage_path:', updatedLayer.storage_path)
  }

  const handleLayerChange = (payload: ChangePayload<any>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    console.log('🔄 [displayLayers] Layer change:', eventType, newRecord || oldRecord)

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      const configId = newRecord?.scene_display_config_id
      if (!configId) return
      
      const currentLayers = layers.value.get(configId) || []
      
      // If we don't have layers for this config yet, we might need to fetch them or just add this one
      // But since we fetch all on load, we should have them.
      
      if (eventType === 'INSERT') {
        const newLayer = cloneDisplayLayer(transformLayer(newRecord))
        const updatedLayers = [...currentLayers, newLayer].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
        layers.value.set(configId, updatedLayers)

        // If this is an image layer with no storage_path, the image_library entry
        // might not be loaded yet (e.g. presenter view in a different tab).
        // Fetch the missing image data and re-resolve the layer.
        if (newRecord.layer_type === 'image' && !newLayer.storage_path && newRecord.content_source) {
          resolveAndUpdateMissingImage(configId, newRecord).catch(err => {
            console.error('❌ [displayLayers] Failed to resolve missing image for new layer:', err)
          })
        }
      } else {
        const updatedLayer = cloneDisplayLayer(transformLayer(newRecord))
        const updatedLayers = currentLayers.map(l => l.id === updatedLayer.id ? updatedLayer : l)
          .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
        layers.value.set(configId, updatedLayers)
      }
    } else if (eventType === 'DELETE') {
      // For delete, we might need to find which config it belonged to if we only have oldRecord
      // oldRecord usually only has ID unless we use replica identity full
      // But we can search our maps
      
      // If we have scene_display_config_id in oldRecord (requires replica identity full or it being part of PK?)
      // Assuming we might not have it, we search
      let configId = oldRecord?.scene_display_config_id
      
      if (!configId) {
        // Search all lists
        for (const [cid, list] of layers.value.entries()) {
          if (list.some(l => l.id === oldRecord?.id)) {
            configId = cid
            break
          }
        }
      }
      
      if (configId && oldRecord) {
        const currentLayers = layers.value.get(configId) || []
        const updatedLayers = currentLayers.filter(l => l.id !== oldRecord.id)
        layers.value.set(configId, updatedLayers)
      }
    }
  }

  const handleOverrideChange = (payload: ChangePayload<any>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    console.log('🔄 [displayLayers] Override change:', eventType, newRecord || oldRecord)

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      const configId = newRecord?.scene_display_config_id
      if (!configId) return
      
      const currentOverrides = overrides.value.get(configId) || []
      
      if (eventType === 'INSERT') {
        overrides.value.set(configId, [...currentOverrides, newRecord])
      } else {
        const updatedOverrides = currentOverrides.map(o => o.id === newRecord.id ? newRecord : o)
        overrides.value.set(configId, updatedOverrides)
      }
    } else if (eventType === 'DELETE') {
      let configId = oldRecord?.scene_display_config_id
      
      if (!configId) {
        for (const [cid, list] of overrides.value.entries()) {
          if (list.some(o => o.id === oldRecord?.id)) {
            configId = cid
            break
          }
        }
      }
      
      if (configId && oldRecord) {
        const currentOverrides = overrides.value.get(configId) || []
        const updatedOverrides = currentOverrides.filter(o => o.id !== oldRecord.id)
        overrides.value.set(configId, updatedOverrides)
      }
    }
  }

  return {
    // State
    layers,
    overrides,
    loading,
    error,
    initialized,

    // Actions
    fetchLayersForDisplay,
    fetchLayersWithInheritance,
    fetchAllLayers,
    createLayer,
    updateLayer,
    deleteLayer,
    reorderLayers,
    duplicateLayer,
    deleteLayers,
    getLayersForDisplay,
    createImageLayer,
    createVideoLayer,
    createYouTubeLayer,
    findLayerById,
    updateLayerZIndexInCache,
    updateOverrideZIndexInCache,
    subscribeToLayerUpdates
  }
})